import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropZone", "preview", "removeCheckbox", "fileName", "fileSize"]
  static values = { 
    hasImage: Boolean,
    currentFilename: String,
    currentSize: String 
  }

  connect() {
    this.setupDragAndDrop()
    // Ensure UI state is correct on load
    if (!this.hasImageValue) {
      this.showEmptyState()
    }
  }

  setupDragAndDrop() {
    this.dropZoneTarget.addEventListener("dragover", this.handleDragOver.bind(this))
    this.dropZoneTarget.addEventListener("dragleave", this.handleDragLeave.bind(this))
    this.dropZoneTarget.addEventListener("drop", this.handleDrop.bind(this))
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropZoneTarget.classList.add("drag-over")
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropZoneTarget.classList.remove("drag-over")
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropZoneTarget.classList.remove("drag-over")
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.inputTarget.files = files
      this.previewImage()
    }
  }

  triggerFileInput(e) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    this.inputTarget.click()
  }

  previewImage() {
    const file = this.inputTarget.files[0]
    
    if (!file) return

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      this.showError("Por favor, selecione apenas imagens JPEG ou PNG")
      this.inputTarget.value = ""
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.showError("A imagem deve ter menos de 5 MB")
      this.inputTarget.value = ""
      return
    }

    const reader = new FileReader()
    
    reader.onload = (e) => {
      // Update preview image
      this.previewTarget.src = e.target.result
      this.previewTarget.classList.remove("d-none")
      
      // Update file info
      if (this.hasFileNameTarget) {
        this.fileNameTarget.textContent = file.name
      }
      if (this.hasFileSizeTarget) {
        this.fileSizeTarget.textContent = this.formatFileSize(file.size)
      }
      
      // Show image state
      this.showImageState()
      
      // Uncheck remove checkbox (user is adding new image)
      if (this.hasRemoveCheckboxTarget) {
        this.removeCheckboxTarget.checked = false
      }
    }

    reader.readAsDataURL(file)
  }

  removeImage(e) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    // Add removing animation
    this.dropZoneTarget.classList.add("removing-animation")
    
    setTimeout(() => {
      // Clear input
      this.inputTarget.value = ""
      
      // Hide preview
      if (this.hasPreviewTarget) {
        this.previewTarget.src = ""
        this.previewTarget.classList.add("d-none")
      }
      
      // Check the remove checkbox for form submission
      if (this.hasRemoveCheckboxTarget) {
        this.removeCheckboxTarget.checked = true
      }
      
      // Show empty state
      this.showEmptyState()
      
      this.dropZoneTarget.classList.remove("removing-animation")
    }, 300)
  }

  showImageState() {
    this.dropZoneTarget.classList.add("has-image")
    this.dropZoneTarget.classList.remove("drag-over")
    
    // Find and show preview wrapper
    const previewWrapper = this.dropZoneTarget.querySelector(".image-preview-wrapper")
    if (previewWrapper) {
      previewWrapper.style.display = "block"
    }
    
    // Hide placeholder
    const placeholder = this.dropZoneTarget.querySelector(".upload-placeholder")
    if (placeholder) {
      placeholder.style.display = "none"
    }
  }

  showEmptyState() {
    this.dropZoneTarget.classList.remove("has-image")
    this.dropZoneTarget.classList.remove("drag-over")
    
    // Hide preview wrapper
    const previewWrapper = this.dropZoneTarget.querySelector(".image-preview-wrapper")
    if (previewWrapper) {
      previewWrapper.style.display = "none"
    }
    
    // Show placeholder
    const placeholder = this.dropZoneTarget.querySelector(".upload-placeholder")
    if (placeholder) {
      placeholder.style.display = "block"
    }
    
    // Hide preview image
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("d-none")
    }
  }

  showError(message) {
    // Create toast notification
    const toast = document.createElement("div")
    toast.className = "toast-notification error"
    toast.innerHTML = `
      <i class="bi bi-exclamation-triangle me-2"></i>
      ${message}
    `
    document.body.appendChild(toast)
    
    setTimeout(() => toast.classList.add("show"), 10)
    setTimeout(() => {
      toast.classList.remove("show")
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i]
  }
}
